<template>
  <sidebar-row slot-cols="11" align-class="align-center" flex-wrap="flex-nowrap">
    <template #actionIcon v-if="hasAddButton">
      <base-rounded-button
        :tooltip="addRowTooltip"
        :disabled="!isValid"
        icon="mdi-plus"
        color="success"
        size="x-small"
        @click="addRow"
      >
      </base-rounded-button>
    </template>
    <v-row no-gutters class="expression mr-4 flex-nowrap align-start">
      <v-col cols="5">
        <data-input
          class="my-0 py-0 ml-3 pt-5"
          :menu-props="{ offsetY: true }"
          :items="computedAttributes"
          :value="attribute"
          @change="valueChanged($event, 'attribute')"
          item-text="label"
          item-value="name"
          :rules="rules"
          :data-type="dataType"
          :label="chooseAttributeLabel"
        >
        </data-input>
      </v-col>
      <v-col cols="1" class="pt-6">
        <span class="text-h5">
          =
        </span>
      </v-col>
      <v-col cols="5">
        <v-row no-gutters class="justify-center">
          <v-col cols="12">
            <v-tooltip bottom color="font" :disabled="isTypeTooltipDisabled">
              <template #activator="{ on }">
                <span v-on="on">
                  <v-radio-group
                    height="16px"
                    :disabled="!attribute"
                    class="my-0 py-0 input--type"
                    :value="type"
                    @change="valueChanged($event, 'type')"
                    :rules="rules"
                    dense
                    row
                    hide-details
                  >
                    <v-radio :label="$i18n.t('dialog.value')" value="value"></v-radio>
                    <v-tooltip bottom color="font" :disabled="!hasAnyAttribute">
                      <template #activator="{ on }">
                        <span v-on="on">
                          <v-radio
                            :disabled="hasAnyAttribute"
                            :label="$i18n.t('dialog.attribute')"
                            value="attribute"
                          ></v-radio>
                        </span>
                      </template>
                      {{ $i18n.t('sidebar.fieldCalculator.noMatchingAttributes') }}
                    </v-tooltip>
                  </v-radio-group>
                </span>
              </template>
              <span>
                {{ attributeRequiredTooltip }}
              </span>
            </v-tooltip>
          </v-col>
        </v-row>
        <v-row no-gutters>
          <v-col>
            <v-tooltip bottom color="font" :disabled="isValueTooltipDisabled">
              <template #activator="{ on }">
                <span v-on="on">
                  <data-input
                    v-bind="valueInputConfig"
                    :height="20"
                    item-value="value"
                    v-model="computedValue"
                    :disabled="!type"
                    :loading="isValueInputLoading"
                    :is-editing-on="valueInputConfig?.dataType?.name === 'hyperlink'"
                  />
                </span>
              </template>
              <span>
                {{ valueTooltip }}
              </span>
            </v-tooltip>
          </v-col>
        </v-row>
      </v-col>
      <v-col cols="1" align-self="center">
        <v-tooltip bottom color="font" :disabled="!isValid">
          <template #activator="{ on }">
            <v-icon
              v-on="on"
              small
              v-if="hasDeleteButton"
              color="rgba(0,0,0,0.59)"
              style="cursor: pointer"
              @click="removeRow"
            >
              mdi-close
            </v-icon>
          </template>
          {{ removeRowTooltip }}
        </v-tooltip>
      </v-col>
    </v-row>
  </sidebar-row>
</template>

<script>
import { get } from 'vuex-pathify';
import DataInput from '@/components/DataInput';
import SidebarRow from '@/components/SidebarRow';

export default {
  name: 'FieldCalculatorRow',
  components: {
    DataInput,
    SidebarRow,
  },
  props: {
    isValid: {
      type: Boolean,
      default: false,
    },
    isLessThanTwoSelected: {
      type: Boolean,
      default: false,
    },
    hasAddButton: {
      type: Boolean,
      default: false,
    },
    hasDeleteButton: {
      type: Boolean,
      default: false,
    },
    attributes: {
      type: Array,
      required: true,
    },
    relationalAttributesValues: {
      type: Array,
      default: () => [],
    },
    attribute: {
      type: String,
      default: null,
    },
    value: {
      type: [String, Number],
      default: null,
    },
    type: {
      type: String,
      default: null,
    },
    index: {
      type: Number,
      required: true,
    },
    isDataSourceUseAttributesQualifiedNames: {
      type: Boolean,
      required: true,
    },
  },
  data: vm => ({
    isValueInputLoading: false,
    rules: [v => !!v || vm.$i18n.t('rules.required')],
    dataType: {
      name: 'select',
    },
  }),
  computed: {
    deleteUserSufix() {
      return `(${this.$store.get('admin/modulesMapping@users.deleted_suffix')})`;
    },
    computedValue: {
      get() {
        return this.value;
      },
      set(nV) {
        this.$emit('change', nV, 'value', this.index);
      },
    },
    relationalValues: get('attributesSchema/relationValuesMapping'),
    computedAttributes() {
      return this.attributes.filter(attribute => !attribute.read_only);
    },
    selectedAttribute() {
      return this.computedAttributes.find(att => att.name === this.attribute);
    },
    isDict() {
      return this.selectedAttribute?.type === 'dict';
    },
    isRelational() {
      return this.selectedAttribute?.type === 'relation';
    },
    isSelect() {
      return this.isDict || this.isRelational;
    },
    selectedAttributeDataType() {
      if (this.isSelect) {
        return this.dataType;
      }
      return (
        this.selectedAttribute?.data_type || {
          name: 'text',
        }
      );
    },
    selectItems() {
      if (this.selectedAttribute?.relation) {
        const { attribute, data_source, representation } = this.selectedAttribute.relation;
        let items = this.relationalValues[data_source]?.[attribute]?.[representation] || [];
        if (data_source === 'users_users') items = items.filter(item => !item.text.includes(this.deleteUserSufix));
        return items;
      }
      return this.selectedAttribute?.allowed_values;
    },
    computedRelationalAttributes() {
      return this.relationalAttributesValues.filter(
        attribute => attribute.data_type.name === this.selectedAttributeDataType.name
      );
    },
    filteredRelationalAttributes() {
      if (this.isRelational) {
        return this.computedRelationalAttributes.filter(
          attribute =>
            attribute.relation?.attribute === this.selectedAttribute.relation.attribute &&
            attribute.relation?.data_source === this.selectedAttribute.relation.data_source &&
            attribute.name !== this.selectedAttribute.name
        );
      }
      if (this.isDict) {
        return this.computedRelationalAttributes.filter(
          attribute =>
            attribute.dict_id &&
            attribute.dict_id === this.selectedAttribute.dict_id &&
            attribute.name !== this.selectedAttribute.name
        );
      }
      return this.computedRelationalAttributes;
    },
    hasAnyAttribute() {
      return this.filteredRelationalAttributes?.length === 0;
    },
    valueInputConfig() {
      return this.type === 'attribute'
        ? {
            itemText: 'label',
            items: this.filteredRelationalAttributes,
            label: this.chooseAttributeLabel,
            dataType: this.dataType,
            rules: this.rules,
          }
        : {
            items: this.selectItems,
            label: this.chooseValueLabel,
            dataType: this.selectedAttributeDataType,
          };
    },
    chooseAttributeLabel() {
      return this.$i18n.t('dialog.body.attribute');
    },
    chooseValueLabel() {
      return this.$i18n.t('dialog.body.value');
    },
    chooseExpressionLabel() {
      return this.$i18n.t('dialog.body.expression');
    },
    isTypeTooltipDisabled() {
      return this.attribute && this.isValid;
    },
    isValueTooltipDisabled() {
      return this.attribute && this.type && this.isValid;
    },
    attributeRequiredTooltip() {
      return this.$i18n.t('tooltips.attrbuteRequired');
    },
    valueTypeRequiredTooltip() {
      return this.$i18n.t('tooltips.valueTypeRequired');
    },
    valueTooltip() {
      return !this.attribute ? this.attributeRequiredTooltip : this.valueTypeRequiredTooltip;
    },
    addRowTooltip() {
      if (this.isLessThanTwoSelected) {
        return;
      }
      return this.isValid
        ? this.$i18n.t('sidebar.fieldCalculator.addRow')
        : this.$i18n.t('sidebar.fieldCalculator.fillCurrentInputs');
    },
    removeRowTooltip() {
      return this.$i18n.t('sidebar.fieldCalculator.deleteRow');
    },
  },
  methods: {
    addRow() {
      this.$emit('addRow');
    },
    removeRow() {
      this.$emit('removeRow', this.index);
    },
    valueChanged(value, key) {
      this.$emit('change', value, key, this.index);
      if (key === 'attribute' && !this.hasAnyAttribute) {
        this.$emit('change', 'value', 'type', this.index);
      }
      if (key === 'type' || key === 'attribute') {
        this.$emit('change', null, 'value', this.index);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.expression {
  background-color: rgba(234, 234, 244, 0.25);
  border: 0.6px solid rgba(0, 0, 0, 0.12);
  min-height: 60px;
  position: relative;
}
::v-deep {
  .v-input--radio-group__input {
    flex-wrap: nowrap;
  }
  .v-list-item__title {
    @include selectListElement();
  }

  .v-input__slot {
    margin-bottom: 2px !important;
  }
  .v-text-field:not(.v-select) .v-input__append-inner {
    margin-top: -1px !important;
  }
  .input--type {
    white-space: nowrap;
    .v-icon {
      font-size: 15px;
    }
    .v-label {
      font-size: 12px;
    }
    .v-radio {
      margin-right: 5px;
    }
    .v-input--selection-controls__ripple {
      width: 15px;
      height: 15px;
      left: -3px;
      top: calc(50% - 15px) !important;
    }
    .v-input--selection-controls__input {
      margin-right: 0;
    }
  }
  .vue-input {
    font-size: 13px;
  }
}
</style>
